import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Card, Heading, Text } from '../ui/index.js';
import type { StorageRow } from '@goodbuild/shared';
import { api } from '../api/client.js';
import { keys, useAccess } from '../api/hooks.js';
import { EmptyState, Loading } from '../components/layout/Loading.js';
import { formatBytes, formatRelative } from '../lib/format.js';

export const Route = createFileRoute('/admin/storage')({ component: AdminStorage });

/**
 * What each game holds in R2, and when garbage collection last ran for it
 * (`05-storage-and-delivery.md` section 5).
 *
 * This is how "old build data is gone from R2 after publishing" is checked: the stored bytes
 * drop after a publish, and the last run says how much went.
 */
function AdminStorage() {
  const { isAdmin } = useAccess();

  const { data, isLoading } = useQuery({
    queryKey: keys.admin.storage,
    queryFn: async () => (await api.get<{ rows: StorageRow[] }>('/admin/storage')).rows,
    enabled: isAdmin,
    refetchInterval: 60_000,
  });

  if (!isAdmin) return <EmptyState title={'You cannot do that.'} />;
  if (isLoading) return <Loading />;

  const rows = data ?? [];
  const total = rows.reduce((sum, row) => sum + row.storedBytes, 0);

  return (
    <div className="gb-stack">
      <Card size="3">
        <Text size="2" color="gray">
          {'Stored'}
        </Text>
        <Heading size="7">{formatBytes(total)}</Heading>
      </Card>

      {rows.length === 0 ? (
        <EmptyState title={'None'} />
      ) : (
        rows.map((row) => (
          <Card key={row.gameId} size="2">
            <div className="gb-seat">
              <Text size="2">{row.title}</Text>
              <span className="gb-seat__spacer" />
              <Text size="1" color="gray">
                {formatBytes(row.storedBytes)} · {row.objectCount} {'Objects'}
              </Text>
              <Text size="1" color="gray">
                {row.lastGcAt ? `Last cleanup: ${formatRelative(row.lastGcAt)}` : 'None'}
              </Text>
              {row.lastGcRemovedBytes ? (
                <Text size="1" color="gray">
                  -{formatBytes(row.lastGcRemovedBytes)}
                </Text>
              ) : null}
            </div>
          </Card>
        ))
      )}
    </div>
  );
}
